import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Calculator,
  FilePdf,
  UploadSimple,
  Spinner,
  CheckCircle,
  WarningCircle,
  XCircle,
  Lock,
  ArrowRight,
  ArrowLeft,
  Sparkle,
  Trash,
  CurrencyEur,
  FileText,
} from "@phosphor-icons/react";
import { AIDisclaimer } from "../components/AIDisclaimer";

const MAX_MB = 8;

function PremiumLock() {
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#FFD600]/30 rounded-md p-6 text-center" data-testid="computo-premium-lock">
      <div className="w-16 h-16 mx-auto bg-[#FFD600]/10 rounded-full flex items-center justify-center mb-4">
        <Lock size={32} weight="fill" className="text-[#FFD600]" />
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight mb-2">
        Funzione <span className="text-[#FFD600]">Premium</span>
      </h2>
      <p className="text-sm text-[#A3A3A3] mb-6 max-w-md mx-auto leading-snug">
        La <b>Verifica Computo AI</b> è inclusa nel piano Premium 15€/mese.
        Carica il PDF dell'ingegnere e l'intelligenza artificiale controlla
        automaticamente quantità, misure reali e totali matematici.
      </p>
      <ul className="text-left text-sm text-[#A3A3A3] max-w-md mx-auto space-y-2 mb-6">
        <li className="flex gap-2"><Sparkle weight="fill" className="text-[#FFD600] mt-0.5 shrink-0" size={16} /> Lettura automatica del PDF (qualsiasi formato)</li>
        <li className="flex gap-2"><Sparkle weight="fill" className="text-[#FFD600] mt-0.5 shrink-0" size={16} /> Verifica matematica riga per riga</li>
        <li className="flex gap-2"><Sparkle weight="fill" className="text-[#FFD600] mt-0.5 shrink-0" size={16} /> Confronto con le misure reali del cantiere</li>
        <li className="flex gap-2"><Sparkle weight="fill" className="text-[#FFD600] mt-0.5 shrink-0" size={16} /> Storico verifiche illimitato</li>
      </ul>
      <Link
        to="/abbonamento"
        data-testid="computo-upgrade-btn"
        className="inline-flex items-center gap-2 bg-[#FFD600] text-[#0A0A0A] h-14 px-6 rounded-md font-black uppercase text-base hover:bg-[#E6C200] transition"
      >
        Passa a Premium · 15€/mese <ArrowRight size={20} weight="bold" />
      </Link>
    </div>
  );
}

function MeasurementInput({ label, unit, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">
        {label} <span className="text-[#666]">({unit})</span>
      </label>
      <input
        data-testid={testid}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none font-mono"
      />
    </div>
  );
}

function AutoPricedSection({ items, grandTotal, onGenerateQuote, generating }) {
  if (!items || items.length === 0) return null;
  const confColor = {
    high: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30",
    medium: "text-[#FFD600] bg-[#FFD600]/10 border-[#FFD600]/30",
    low: "text-[#FF9500] bg-[#FF9500]/10 border-[#FF9500]/30",
    none: "text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/30",
  };
  const confLabel = { high: "Alta", medium: "Media", low: "Bassa", none: "Nessun match" };
  return (
    <div className="bg-[#FFD600]/5 border-2 border-[#FFD600]/40 rounded-md p-4" data-testid="auto-priced-section">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <CurrencyEur size={22} weight="fill" className="text-[#FFD600]" />
          <div className="text-xs uppercase tracking-wider text-[#FFD600] font-bold">Prezzatura automatica dal listino</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase text-[#A3A3A3]">Totale ricalcolato</div>
          <div className="font-mono text-lg font-black text-[#FFD600]">€ {(grandTotal || 0).toFixed(2)}</div>
        </div>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {items.map((it, idx) => (
          <div key={`${it.matched_code || "noc"}-${it.pdf_description}-${idx}`} className="p-3 rounded-md bg-[#0A0A0A] border border-[#333] text-sm">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="font-bold text-white flex-1 min-w-0 truncate">{it.pdf_description}</div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${confColor[it.match_confidence] || confColor.none}`}>
                {confLabel[it.match_confidence] || it.match_confidence}
              </span>
            </div>
            <div className="text-xs text-[#A3A3A3] font-mono mb-1.5">
              PDF: {it.pdf_quantity} {it.pdf_unit}
            </div>
            {it.matched_code ? (
              <div className="bg-[#FFD600]/5 border border-[#FFD600]/20 rounded p-2 text-xs">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[#FFD600] font-mono">{it.matched_code}</span>
                  <span className="font-mono font-bold text-[#FFD600]">€ {(it.auto_total || 0).toFixed(2)}</span>
                </div>
                <div className="text-[#A3A3A3] truncate">{it.matched_description}</div>
                <div className="text-[#A3A3A3] font-mono mt-0.5">
                  € {(it.listino_unit_price || 0).toFixed(2)} / {it.matched_unit}
                </div>
              </div>
            ) : (
              <div className="text-xs text-[#FF3B30]">Nessuna voce nel listino</div>
            )}
            {it.match_reason && (
              <div className="text-[10px] text-[#A3A3A3] italic mt-1.5">→ {it.match_reason}</div>
            )}
          </div>
        ))}
      </div>

      {onGenerateQuote && (
        <button
          onClick={onGenerateQuote}
          disabled={generating}
          data-testid="computo-to-quote-btn"
          className="w-full mt-4 h-14 rounded-md bg-[#FFD600] text-[#0A0A0A] font-black uppercase text-base hover:bg-[#E6C200] transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {generating ? (
            <><Spinner size={20} weight="bold" className="animate-spin" /> Creazione preventivo…</>
          ) : (
            <><FileText size={20} weight="bold" /> Genera preventivo modificabile</>
          )}
        </button>
      )}
    </div>
  );
}

function ResultCard({ result, onGenerateQuote, generating, runId }) {
  if (!result) return null;
  const items = result.items || [];
  const totals = result.totals || {};
  const mChecks = result.measurement_checks || [];
  const warns = result.sector_warnings || [];

  const mathErrors = items.filter((i) => !i.math_ok);
  const measErrors = mChecks.filter((m) => !m.ok);

  return (
    <div className="space-y-4" data-testid="computo-result">
      <div className={`rounded-md p-4 border-2 ${(mathErrors.length === 0 && measErrors.length === 0) ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-[#FF3B30]/40 bg-[#FF3B30]/5"}`}>
        <div className="flex items-center gap-3 mb-2">
          {mathErrors.length === 0 && measErrors.length === 0 ? (
            <CheckCircle size={28} weight="fill" className="text-[#22C55E]" />
          ) : (
            <WarningCircle size={28} weight="fill" className="text-[#FF3B30]" />
          )}
          <div className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight">
            {mathErrors.length === 0 && measErrors.length === 0 ? "Verifica OK" : `${mathErrors.length + measErrors.length} Anomalie`}
          </div>
        </div>
        <p className="text-sm text-[#A3A3A3]">{result.summary_it}</p>
      </div>

      {Object.keys(totals).length > 0 && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2">Totali</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[#A3A3A3] text-xs">Dichiarato</div>
              <div className="font-mono text-base">€ {(totals.declared_grand_total || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Calcolato AI</div>
              <div className="font-mono text-base text-[#FFD600]">€ {(totals.computed_grand_total || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Δ</div>
              <div className={`font-mono text-base ${Math.abs(totals.delta_eur || 0) < 0.01 ? "text-[#22C55E]" : "text-[#FF3B30]"}`}>
                € {(totals.delta_eur || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}

      {mChecks.length > 0 && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-3">Misure reali vs PDF</div>
          <div className="space-y-2">
            {mChecks.map((m, idx) => (
              <div key={`${m.label}-${idx}`} className={`flex items-center justify-between p-3 rounded-md ${m.ok ? "bg-[#22C55E]/5 border border-[#22C55E]/20" : "bg-[#FF3B30]/5 border border-[#FF3B30]/30"}`}>
                <div className="flex items-center gap-2">
                  {m.ok ? <CheckCircle weight="fill" className="text-[#22C55E]" /> : <XCircle weight="fill" className="text-[#FF3B30]" />}
                  <span className="font-bold text-sm">{m.label}</span>
                </div>
                <div className="text-xs text-right">
                  <div className="text-[#A3A3A3]">PDF: <span className="font-mono text-white">{m.pdf_value} {m.unit}</span></div>
                  <div className="text-[#A3A3A3]">Reale: <span className="font-mono text-white">{m.user_value} {m.unit}</span></div>
                  {!m.ok && <div className="text-[#FF3B30] font-bold mt-0.5">Δ {(m.delta_pct || 0).toFixed(1)}%</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4">
        <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-3">Righe del computo ({items.length})</div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {items.map((it, idx) => (
            <div key={`${it.code || "x"}-${it.description}-${idx}`} className={`p-3 rounded-md text-sm ${it.math_ok ? "bg-[#0A0A0A] border border-[#333]" : "bg-[#FF3B30]/5 border-2 border-[#FF3B30]/40"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{it.code ? <span className="text-[#FFD600] mr-2">{it.code}</span> : null}{it.description}</div>
                  <div className="text-xs text-[#A3A3A3] mt-0.5 font-mono">
                    {it.quantity} {it.unit} × € {(it.unit_price || 0).toFixed(2)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm">€ {(it.declared_total || 0).toFixed(2)}</div>
                  {!it.math_ok && (
                    <div className="text-[#FF3B30] text-xs font-bold mt-0.5">
                      → € {(it.computed_total || 0).toFixed(2)}<br />
                      Δ {(it.math_error_eur || 0).toFixed(2)}€
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {warns.length > 0 && (
        <div className="bg-[#FFD600]/5 border-2 border-[#FFD600]/40 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-[#FFD600] font-bold mb-2">Note AI</div>
          <ul className="text-sm space-y-1 text-[#A3A3A3]">
            {warns.map((w, i) => <li key={`${w}-${i}`}>· {w}</li>)}
          </ul>
        </div>
      )}

      <AutoPricedSection
        items={result.auto_priced_items}
        grandTotal={result.auto_priced_grand_total}
        onGenerateQuote={runId ? onGenerateQuote : null}
        generating={generating}
      />
    </div>
  );
}

export default function ComputoVerify() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);

  const [file, setFile] = useState(null);
  const [sqmTotal, setSqmTotal] = useState("");
  const [wallLength, setWallLength] = useState("");
  const [height, setHeight] = useState("");
  const [perimeter, setPerimeter] = useState("");
  const [notes, setNotes] = useState("");

  const [verifying, setVerifying] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [autoPrice, setAutoPrice] = useState(false);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const fileInputRef = useRef(null);

  const generateQuoteFromRun = async () => {
    if (!currentRunId) return;
    setGeneratingQuote(true);
    try {
      const { data } = await api.post(`/computo/${currentRunId}/to-quote`);
      toast.success(`Preventivo creato (${data.items_count} voci)`);
      nav(`/preventivi/${data.id}/modifica`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione preventivo");
    } finally {
      setGeneratingQuote(false);
    }
  };

  useEffect(() => {
    api.get("/computo/quota").then(({ data }) => setQuota(data)).catch(() => {});
    api.get("/computo/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Carica un file PDF");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`PDF troppo grande (max ${MAX_MB} MB)`);
      return;
    }
    setFile(f);
  };

  const runVerify = async () => {
    if (!file) return toast.error("Carica un PDF prima di procedere");
    setVerifying(true);
    setCurrentResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (sqmTotal) fd.append("sqm_total", sqmTotal);
      if (wallLength) fd.append("wall_length_ml", wallLength);
      if (height) fd.append("height_m", height);
      if (perimeter) fd.append("perimeter_ml", perimeter);
      if (notes) fd.append("notes", notes);
      if (autoPrice) fd.append("auto_price", "true");
      const { data } = await api.post("/computo/verify", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setCurrentResult(data.result);
      setCurrentRunId(data.id);
      const refreshed = await api.get("/computo/history");
      setHistory(refreshed.data);
      toast.success("Verifica completata");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore verifica AI";
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const loadFromHistory = async (id) => {
    try {
      const { data } = await api.get(`/computo/${id}`);
      setCurrentResult(data.result);
      setCurrentRunId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Impossibile caricare la verifica");
    }
  };

  // Loading state
  if (quota === null) {
    return <Layout><div className="text-[#A3A3A3]">Caricamento…</div></Layout>;
  }

  // Premium lock
  if (!quota.premium) {
    return (
      <Layout>
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">AI Premium</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-1">
            Verifica Computo
          </h1>
        </div>
        <PremiumLock />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD600] font-bold flex items-center gap-1">
            <Sparkle weight="fill" size={14} /> AI Premium
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-1">
            Verifica Computo
          </h1>
        </div>
        <Calculator size={48} weight="fill" className="text-[#FFD600]/30" />
      </div>

      <AIDisclaimer compact />

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-3">1. Carica il PDF del computo</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={onPickFile}
          className="hidden"
          data-testid="computo-file-input"
        />
        {!file ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="computo-pick-btn"
            className="w-full h-32 rounded-md border-2 border-dashed border-[#333] hover:border-[#FFD600] hover:bg-[#FFD600]/5 transition flex flex-col items-center justify-center gap-2 text-[#A3A3A3] hover:text-[#FFD600]"
          >
            <UploadSimple size={32} weight="bold" />
            <span className="text-sm font-bold uppercase">Seleziona PDF</span>
            <span className="text-xs">Max {MAX_MB} MB</span>
          </button>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#333] rounded-md">
            <FilePdf size={32} weight="fill" className="text-[#FF3B30]" />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate text-sm" data-testid="computo-file-name">{file.name}</div>
              <div className="text-xs text-[#A3A3A3]">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
            <button
              onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="p-2 text-[#A3A3A3] hover:text-[#FF3B30]"
              data-testid="computo-clear-file-btn"
            >
              <Trash size={18} weight="bold" />
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1">2. Misure reali del cantiere</div>
        <div className="text-xs text-[#A3A3A3] mb-3">Inserisci almeno una misura per confronto. Vuoto = solo verifica matematica.</div>
        <div className="grid grid-cols-2 gap-3">
          <MeasurementInput label="Superficie totale" unit="mq" value={sqmTotal} onChange={setSqmTotal} testid="computo-sqm-input" />
          <MeasurementInput label="Lunghezza pareti" unit="ml" value={wallLength} onChange={setWallLength} testid="computo-wall-input" />
          <MeasurementInput label="Altezza" unit="m" value={height} onChange={setHeight} testid="computo-height-input" />
          <MeasurementInput label="Perimetro" unit="ml" value={perimeter} onChange={setPerimeter} testid="computo-perimeter-input" />
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">Note</label>
          <input
            data-testid="computo-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Es. ristrutturazione interna 2 locali"
            className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 text-white focus:border-[#FFD600] focus:outline-none"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setAutoPrice(!autoPrice)}
        data-testid="computo-autoprice-toggle"
        className={`w-full mb-4 p-4 rounded-md border-2 text-left transition flex items-center gap-3 ${autoPrice ? "border-[#FFD600] bg-[#FFD600]/5" : "border-[#333] bg-[#1A1A1A] hover:border-[#666]"}`}
      >
        <div className={`w-12 h-7 rounded-full relative transition shrink-0 ${autoPrice ? "bg-[#FFD600]" : "bg-[#333]"}`}>
          <div className={`absolute top-1 w-5 h-5 rounded-full bg-[#0A0A0A] transition ${autoPrice ? "right-1" : "left-1"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 font-bold uppercase text-sm">
            <CurrencyEur size={16} weight="bold" className={autoPrice ? "text-[#FFD600]" : "text-[#A3A3A3]"} />
            Prezzatura automatica dal listino
          </div>
          <div className="text-xs text-[#A3A3A3] mt-0.5 leading-snug">
            L'AI cerca ogni voce del PDF nel tuo listino DEI e ricalcola i totali con i prezzi corretti.
          </div>
        </div>
      </button>

      <button
        onClick={runVerify}
        disabled={!file || verifying}
        data-testid="computo-verify-btn"
        className="w-full h-16 rounded-md bg-[#FFD600] text-[#0A0A0A] font-black uppercase text-lg hover:bg-[#E6C200] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3 mb-6"
      >
        {verifying ? (
          <><Spinner size={24} weight="bold" className="animate-spin" /> Verifica AI in corso…</>
        ) : (
          <><Sparkle size={22} weight="fill" /> {autoPrice ? "Verifica + Prezzatura AI" : "Verifica con AI"}</>
        )}
      </button>

      {currentResult && (
        <ResultCard
          result={currentResult}
          runId={currentRunId}
          onGenerateQuote={generateQuoteFromRun}
          generating={generatingQuote}
        />
      )}

      {history.length > 0 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-3">Storico verifiche ({history.length})</div>
          <div className="space-y-2">
            {history.map((h) => (
              <button
                key={h.id}
                onClick={() => loadFromHistory(h.id)}
                data-testid={`computo-history-${h.id}`}
                className="w-full flex items-center justify-between p-3 bg-[#1A1A1A] border border-[#333] rounded-md hover:border-[#FFD600] transition text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FilePdf size={20} weight="fill" className="text-[#FF3B30] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{h.filename}</div>
                    <div className="text-xs text-[#A3A3A3]">{new Date(h.created_at).toLocaleString("it-IT")}</div>
                  </div>
                </div>
                <ArrowRight size={18} weight="bold" className="text-[#A3A3A3]" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link to="/dashboard" className="text-[#A3A3A3] text-sm flex items-center gap-1 hover:text-white">
          <ArrowLeft size={16} weight="bold" /> Torna alla home
        </Link>
      </div>
    </Layout>
  );
}
